import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { createColumnHelper } from '@tanstack/react-table'
import { addDays, addMonths, format, isEqual, isWeekend, startOfMonth } from 'date-fns'
import { useState } from 'react'
import { LinkButtonReactRouter } from '~/components/button'
import EmptyState from '~/components/empty-state'
import { RouterOutput, useTRPC } from '~/services/trpc'
import { SettingsContent, useSettingTitle } from '~/templates/settings'

import { AUTO_WIDTH, Badge, Button, DataTable, Heading2, Icon } from '@hap/components'
import { formatNumberToCurrency } from '@hap/utils'

export const Route = createFileRoute('/_authenticated/$alias/settings/billing/')({
  component: BillingSettings,
})

function BillingSettings() {
  const trpc = useTRPC()
  const { data: paymentHistory, isInitialLoading } = useQuery(
    trpc.billing.paymentHistory.queryOptions(),
  )
  const [loading, setLoading] = useState(false)
  const mutator = useMutation(trpc.billing.billingPortalSession.mutationOptions())

  useSettingTitle(['Billing'])

  return (
    <SettingsContent className="w-full">
      <div className="grid max-w-[500px] shrink grow items-center gap-4 rounded-md border p-3 md:flex">
        <div className="grow">
          <h3 className="font-semibold">Next Invoice Date</h3>
          <p className="text-2xl font-bold">{getFirstBusinessDayNextMonth()}</p>
        </div>{' '}
        <Button
          variant="secondary"
          loading={loading}
          onClick={() => {
            setLoading(true)
            mutator
              .mutateAsync()
              .then((url) => window.open(url, '_blank'))
              .finally(() => {
                setLoading(false)
              })
          }}
        >
          Update Payment Method
        </Button>
      </div>

      <div className="space-y-6">
        <Heading2>Payment History</Heading2>
        {isInitialLoading ? (
          <DataTable
            data={[]}
            loading={true}
            columns={columns}
            showHeader={false}
            className="border-0"
          />
        ) : paymentHistory?.length ? (
          <DataTable
            data={paymentHistory}
            columns={columns}
            showHeader={false}
            className="border-0"
          />
        ) : (
          <EmptyState
            icon={<Icon name="currency-circle-dollar" size={32} />}
            title="No invoices yet"
            description="Your first invoice will appear here after your billing cycle begins."
          />
        )}
      </div>
    </SettingsContent>
  )
}

const columnHelper = createColumnHelper<RouterOutput['billing']['paymentHistory'][0]>()

const columns = [
  columnHelper.accessor('paid', {
    size: 50,
    cell: (info) => (
      <div>
        <Badge
          variant={info.getValue() ? 'success' : 'destructive'}
          className="inline-flex pl-0"
        >
          {info.getValue() ? (
            <>
              <Icon name="check" className="h-4 stroke-[3px]" /> Paid
            </>
          ) : (
            <>
              <Icon name="x" /> Past Due
            </>
          )}
        </Badge>
      </div>
    ),
  }),
  columnHelper.accessor('periodEnd', {
    size: 120,
    cell: (info) => format(info.getValue() * 1000, 'MMM d, yyyy'),
  }),
  columnHelper.display({
    size: AUTO_WIDTH,
    id: 'total',
    cell: (props) => (
      <span className="text-right font-semibold">
        {formatNumberToCurrency(props.row.original.total, props.row.original.currency)}
      </span>
    ),
  }),
  columnHelper.accessor('hostedInvoiceUrl', {
    size: 1,
    cell: (info) => {
      const url = info.getValue()
      return url ? (
        <LinkButtonReactRouter
          href={url}
          variant="ghost"
          target="_blank"
          size="icon"
          icon="download-simple"
        />
      ) : null
    },
  }),
]

const holidays = [
  new Date(2024, 0, 1),
  new Date(2024, 0, 15),
  new Date(2024, 1, 19),
  new Date(2024, 4, 27),
  new Date(2024, 6, 4),
  new Date(2024, 8, 2),
  new Date(2024, 10, 11),
  new Date(2024, 10, 28),
  new Date(2024, 11, 25),
]

const isHoliday = (date: Date) => {
  return holidays.some((holiday) => isEqual(holiday, date))
}

const getFirstBusinessDayNextMonth = () => {
  const today = new Date()
  let firstDayNextMonth = startOfMonth(addMonths(today, 1))

  while (isWeekend(firstDayNextMonth) || isHoliday(firstDayNextMonth)) {
    firstDayNextMonth = addDays(firstDayNextMonth, 1)
  }

  return format(firstDayNextMonth, 'MMM d, yyyy')
}
